import { calculateDraftKingsBaseline, calculatePwgScore } from "./pwgScore";

const actualKey = (player) => `${player.name}|${player.team}`.toLowerCase();
const eligible = (player, slot) => player.rosterPositions.includes(slot);
const defaultSalaryFloor = (contract) => contract.contestType === "SHOWDOWN" ? 45000 : 49000;

export function validateLineup(contract, lineup, settings = {}) {
  const errors = [];
  if (!lineup || lineup.slots.length !== contract.lineupHeader.length) errors.push("Incorrect roster size.");
  const identities = new Set();
  let salary = 0;
  (lineup?.slots || []).forEach(({ slot, player }) => {
    if (!player) { errors.push(`${slot} is empty.`); return; }
    if (!eligible(player, slot)) errors.push(`${player.name} is not eligible at ${slot}.`);
    if (identities.has(actualKey(player))) errors.push(`${player.name} appears more than once.`);
    identities.add(actualKey(player)); salary += player.salary;
  });
  const minimumSalary = Number(settings.minimumSalary) || 0;
  if (salary > contract.salaryCap) errors.push(`Salary ${salary} exceeds ${contract.salaryCap}.`);
  if (salary < minimumSalary) errors.push(`Salary ${salary} is below the required minimum ${minimumSalary}.`);
  return { valid: errors.length === 0, errors, salary };
}

function difference(left, right) {
  const a = new Set(left.slots.map(({ player }) => actualKey(player)));
  const b = new Set(right.slots.map(({ player }) => actualKey(player)));
  return [...a].filter((key) => !b.has(key)).length;
}

export function validatePortfolio(contract, lineups, settings = {}) {
  const errors = [];
  const exposure = new Map();
  lineups.forEach((lineup, index) => {
    const result = validateLineup(contract, lineup, settings);
    result.errors.forEach((error) => errors.push(`Lineup ${index + 1}: ${error}`));
    new Set(lineup.slots.map(({ player }) => actualKey(player))).forEach((key) => exposure.set(key, (exposure.get(key) || 0) + 1));
    lineups.slice(0, index).forEach((other, otherIndex) => {
      if (difference(lineup, other) < settings.minUnique) errors.push(`Lineups ${otherIndex + 1} and ${index + 1} violate minimum uniqueness.`);
    });
  });
  const maxUses = Math.max(1, Math.floor(((Number(settings.maxExposure) || 100) / 100) * lineups.length));
  exposure.forEach((uses, key) => { if (uses > maxUses) errors.push(`${key} exceeds maximum exposure.`); });
  return { valid: errors.length === 0, errors, exposure };
}

function enrichedPool(contract, matchReport, excluded) {
  return matchReport.results.filter(({ player }) => !excluded.has(actualKey(player))).map((result) => {
    const baseline = calculateDraftKingsBaseline(result.player, contract.contestType);
    if (result.status === "MATCHED") return {
      ...result.player, pwgScore: calculatePwgScore(result.pwgRows, baseline),
      scoringSource: "PWG Enriched", matchStatus: result.status,
    };
    return {
      ...result.player, pwgScore: baseline,
      scoringSource: result.status === "AMBIGUOUS" ? "Ambiguous—No PWG Enrichment" : "DraftKings Only",
      matchStatus: result.status,
    };
  });
}

function candidateSubset(pool, slot, locked, uses, maxUses) {
  const available = pool.filter((player) => eligible(player, slot) && (uses.get(actualKey(player)) || 0) < maxUses);
  const byScore = [...available].sort((a, b) => b.pwgScore - a.pwgScore);
  const byValue = [...available].sort((a, b) => (b.pwgScore / b.salary) - (a.pwgScore / a.salary));
  const bySalary = [...available].sort((a, b) => a.salary - b.salary);
  const selected = new Map();
  [...available.filter((p) => locked.has(actualKey(p))), ...byScore.slice(0, 90), ...byValue.slice(0, 50), ...bySalary.slice(0, 35)]
    .forEach((player) => selected.set(player.id, player));
  return [...selected.values()];
}

function searchLineups(contract, pool, settings, priorLineups, uses, maxUses) {
  const slots = contract.lineupHeader.map((slot, index) => ({ slot, index }));
  const candidates = new Map(slots.map(({ slot }) => [slot, candidateSubset(pool, slot, settings.locked, uses, maxUses)]));
  const ordered = [...slots].sort((a, b) => candidates.get(a.slot).length - candidates.get(b.slot).length);
  let states = [{ picks: [], identities: new Set(), salary: 0, score: 0 }];
  for (const { slot, index } of ordered) {
    const next = [];
    states.forEach((state) => candidates.get(slot).forEach((player) => {
      const key = actualKey(player);
      if (state.identities.has(key) || state.salary + player.salary > contract.salaryCap) return;
      const identities = new Set(state.identities); identities.add(key);
      next.push({
        picks: [...state.picks, { slot, index, player }], identities,
        salary: state.salary + player.salary,
        score: state.score + player.pwgScore,
      });
    }));
    const best = new Map();
    const ranked = next.sort((a, b) => b.score - a.score || b.salary - a.salary);
    ranked.forEach((state) => {
      const signature = [...state.identities].sort().join("|");
      if (!best.has(signature)) best.set(signature, state);
    });
    const unique = [...best.values()];
    const salaryBands = new Map();
    unique.forEach((state) => {
      const band = Math.floor(state.salary / 100);
      const entries = salaryBands.get(band) || [];
      if (entries.length < 4) entries.push(state);
      salaryBands.set(band, entries);
    });
    const retained = new Map();
    [...unique.slice(0, 600), ...[...salaryBands.values()].flat()].forEach((state) => {
      retained.set([...state.identities].sort().join("|"), state);
    });
    states = [...retained.values()].slice(0, 2600);
    if (!states.length) break;
  }
  return states.filter((state) => state.salary >= settings.minimumSalary
      && [...settings.locked].every((key) => state.identities.has(key)))
    .map((state) => ({
      slots: state.picks.sort((a, b) => a.index - b.index).map(({ slot, player }) => ({ slot, player })),
      score: state.score,
    }))
    .filter((lineup) => priorLineups.every((other) => difference(lineup, other) >= settings.minUnique))
    .sort((a, b) => b.score - a.score || validateLineup(contract, b).salary - validateLineup(contract, a).salary);
}

function infeasibilityReason(contract, pool, settings) {
  for (const slot of [...new Set(contract.lineupHeader)]) {
    const required = contract.lineupHeader.filter((value) => value === slot).length;
    const available = new Set(pool.filter((player) => eligible(player, slot)).map(actualKey)).size;
    if (available < required) return `${slot} requires ${required} eligible players but only ${available} remain after exclusions and exposure limits.`;
  }
  if (settings.locked.size > contract.lineupHeader.length) return `${settings.locked.size} locks exceed the ${contract.lineupHeader.length} roster slots.`;
  if (settings.minimumSalary > contract.salaryCap) return `Minimum salary ${settings.minimumSalary} exceeds the ${contract.salaryCap} salary cap.`;
  return `No lineup satisfies the $${settings.minimumSalary.toLocaleString()} salary minimum, $${contract.salaryCap.toLocaleString()} cap, locks, exclusions, exposure, and uniqueness together.`;
}

export function generateDraftKingsLineups(contract, matchReport, rawSettings = {}) {
  const count = Math.max(1, Math.min(150, Number(rawSettings.count) || 1));
  const minUnique = Math.max(0, Math.min(contract.lineupHeader.length, Number(rawSettings.minUnique) || 0));
  const maxExposure = Math.max(1, Math.min(100, Number(rawSettings.maxExposure) || 100));
  const requestedMinimum = rawSettings.minimumSalary === undefined || rawSettings.minimumSalary === null || rawSettings.minimumSalary === ""
    ? defaultSalaryFloor(contract) : Number(rawSettings.minimumSalary);
  const minimumSalary = Math.max(0, Math.min(contract.salaryCap + 1, Number.isFinite(requestedMinimum) ? requestedMinimum : defaultSalaryFloor(contract)));
  const settings = { count, minUnique, maxExposure, minimumSalary, locked: new Set(rawSettings.locked || []) };
  const excluded = new Set(rawSettings.excluded || []);
  const pool = enrichedPool(contract, matchReport, excluded);
  const maxUses = Math.max(1, Math.floor((maxExposure / 100) * count));
  const uses = new Map(), lineups = [];
  while (lineups.length < count) {
    const candidates = searchLineups(contract, pool, settings, lineups, uses, maxUses);
    if (!candidates.length) break;
    const lineup = candidates[0];
    lineups.push(lineup);
    new Set(lineup.slots.map(({ player }) => actualKey(player))).forEach((key) => uses.set(key, (uses.get(key) || 0) + 1));
  }
  const normalizedSettings = { count, minUnique, maxExposure, minimumSalary };
  const validation = validatePortfolio(contract, lineups, normalizedSettings);
  if (lineups.length < count) validation.errors.push(`Generated ${lineups.length} of ${count}: ${infeasibilityReason(contract, pool, settings)}`);
  return { lineups, validation, settings: normalizedSettings };
}

export { actualKey, defaultSalaryFloor };
